import { useRef, useState } from 'react'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalButton, MinimalInput } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { z } from 'zod'
import { commandPayload, commands } from '../../lib/runtime'
import type { KitchenBill } from '../choosechow/cartQuote'

import { promoCartSignature, type AppliedPromo } from './promotionModel'
const quoteSchema = z.object({ promo_code: z.string(), subtotal_minor: z.coerce.number().int().safe(), discount_minor: z.coerce.number().int().nonnegative().safe() })
const Promo = styled.div`
  display: grid;
  gap: ${minimalVars.space['2xs']};
  padding: ${minimalVars.space.xs} 0;
  border-top: 1px solid ${minimalVars.color.borderSubtle};
  p { margin: 0; color: ${minimalVars.color.textTertiary}; font-size: ${minimalVars.typography.captionSize}; line-height: 1.4; }
`
const PromoRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: ${minimalVars.space['2xs']};
  > button { min-height: 44px; }
`

export const CartPromoField = ({ bill, profileId, applied, disabled, onApply }: {
  bill: KitchenBill; profileId: string; applied?: AppliedPromo; disabled: boolean; onApply: (promo?: AppliedPromo) => void
}) => {
  const [code, setCode] = useState(applied?.code ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pending = useRef(false)
  const apply = async () => {
    if (pending.current || !code.trim()) return
    pending.current = true; setBusy(true); setError('')
    try {
      const quote = quoteSchema.parse(commandPayload(await commands.promotions.quote({
        promo_code: code.trim(), customer_profile_id: profileId, merchant_profile_id: bill.merchantId,
      })))
      if (quote.subtotal_minor !== bill.subtotalMinor || quote.discount_minor > bill.subtotalMinor) throw new Error('Your cart changed. Review it and apply the code again.')
      onApply({ code: quote.promo_code, discountMinor: quote.discount_minor, signature: promoCartSignature(bill) })
    } catch (err) { onApply(undefined); setError(err instanceof Error ? err.message : 'Could not validate promo code.') }
    finally { pending.current = false; setBusy(false) }
  }
  return <Promo>
    <PromoRow>
      <MinimalInput label="Promo code" placeholder="Enter code" value={code} maxLength={32} disabled={busy || disabled} onChange={(e) => { setCode(e.target.value); onApply(undefined); setError('') }} />
      <MinimalButton type="button" size="sm" disabled={busy || disabled || !code.trim()} onClick={() => void apply()}>{busy ? 'Checking…' : 'Apply code'}</MinimalButton>
    </PromoRow>
    {applied && <>
      <p role="status">{applied.code} · Save ₦{(applied.discountMinor / 100).toLocaleString('en-NG')}</p>
      <MinimalButton type="button" variant="ghost" disabled={disabled || busy} onClick={() => { setCode(''); onApply(undefined) }}>Remove code</MinimalButton>
    </>}
    {error && <MinimalAlert tone="danger" title="Promo not applied">{error}</MinimalAlert>}
    <p>Discounts apply to this kitchen’s food. The service fee still applies.</p>
  </Promo>
}
